import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/TMRWMember/2Photobooth.css';

function ResultScreenTMRWMember() {
  const location = useLocation();
  const navigate = useNavigate();
  const { images = [], selectedFrame } = location.state || {}; // Get selected frame
  const canvasRef = useRef(null);
  const [canvasReady, setCanvasReady] = useState(false);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas not found');
      return;
    }

    try {
      // Check if canvas has content
      const imageData = canvas.toDataURL('image/png');
      if (imageData === 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==') {
        console.error('Canvas appears to be empty');
        return;
      }

      const link = document.createElement('a');
      link.download = 'photostrip.png';
      link.href = imageData;
      
      // Ensure the link is added to the DOM and clicked
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Download triggered successfully');
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  // Implementation with proper image placement
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    console.log('Device detection - isMobile:', isMobile, 'userAgent:', navigator.userAgent);
    
    // Helper function to calculate source crop dimensions for an image to fill target area
    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        // Image is taller than target - crop top and bottom
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        // Image is wider than target - crop left and right
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };

    // Helper function to draw placeholders
    function drawPlaceholder(area) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(area.x, area.y, area.width, area.height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', area.x + area.width/2, area.y + area.height/2);
    }

    // On mobile, use simpler frame loading approach
    if (isMobile) {
      console.log('Mobile detected, using mobile-optimized layout');
      
      // Load frame first to get proper dimensions
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage || '/img/LLMember/Yeonjun_LL.png';
      
      frameImg.onload = () => {
        console.log('Mobile frame loaded:', frameImg.src);
        
        // Scale down for mobile but keep reasonable resolution
        const maxWidth = Math.min(600, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);
        
        canvas.width = mobileWidth;
        canvas.height = mobileHeight;
        
        console.log('Mobile canvas dimensions:', canvas.width, 'x', canvas.height);
        
        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Define photo areas scaled for mobile
        const photoWidth = mobileWidth * 0.91;
        const photoHeight = photoWidth / (4/3);
        const photoX = (mobileWidth - photoWidth) / 2;
        const topMargin = mobileHeight * 0.055;
        const spacing = mobileHeight * 0.002;
        
        const photoAreas = [
          { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
        ];
        
        let imagesLoaded = 0;
        const totalImages = images.length;
        
        // Draw photos
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const area = photoAreas[i];
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth,
                img.naturalHeight,
                area.width,
                area.height
              );
              ctx.drawImage(
                img,
                sourceX, sourceY, sourceWidth, sourceHeight,
                area.x, area.y, area.width, area.height
              );
              
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                // Draw frame on top
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
                setCanvasReady(true);
              }
            };
            img.onerror = () => {
              drawPlaceholder(area);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
                setCanvasReady(true);
              }
            };
          } else {
            drawPlaceholder(area);
          }
        }
        
        // If no images, draw frame immediately
        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
          setCanvasReady(true);
        }
      };
      
      frameImg.onerror = () => {
        console.error('Mobile frame loading failed, using simple layout');
        // Fallback to simple stacked layout
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const y = i * mobileHeight;
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              ctx.drawImage(img, 0, y, mobileWidth, mobileHeight);
            };
          }
        }
        
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
        setCanvasReady(true);
      };
      
      return; // Skip desktop frame loading
    }

    // First, load the frame to get its natural dimensions
    const frameImg = new window.Image();
    frameImg.src = selectedFrame?.frameImage || '/img/LLMember/Yeonjun_LL.png';
    
    console.log('Attempting to load frame image:', frameImg.src);
    
    frameImg.onload = () => {
      console.log('Frame image loaded successfully:', frameImg.src);
      // Set canvas dimensions to match the frame's natural dimensions
      canvas.width = frameImg.naturalWidth;
      canvas.height = frameImg.naturalHeight;
      
      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Define photo areas within the frame based on the actual frame layout
      const frameWidth = frameImg.naturalWidth;
      const frameHeight = frameImg.naturalHeight;
      
      // Calculate photo areas to match the 4:3 aspect ratio used in the booth
      const photoAspectRatio = 4/3; // Same as booth webcam
      const photoWidth = frameWidth * 0.91; // Same as ConfirmLLMember
      const photoHeight = photoWidth / photoAspectRatio; // Maintain 4:3 aspect ratio
      const photoX = (frameWidth - photoWidth) / 2; // Center horizontally
      
      // Vertical positioning - evenly spaced photo areas
      const topMargin = frameHeight * 0.055; // Top margin
      const spacing = frameHeight * 0.002; // Space between photos
      
      const photoAreas = [
        { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
      ];

      // Track how many images have been loaded
      let imagesLoaded = 0;
      const totalImages = images.length;

      // Function to draw frame after all images are loaded
      const drawFrameOnTop = () => {
        ctx.drawImage(frameImg, 0, 0);
        setCanvasReady(true); // Mark canvas as ready after frame is drawn
      };

      // Draw photos FIRST (behind the frame)
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const area = photoAreas[i];
        
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              area.width,
              area.height
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              area.x, area.y, area.width, area.height
            );
            
            imagesLoaded++;
            // Draw frame on top after all images are loaded
            if (imagesLoaded === totalImages) {
              drawFrameOnTop();
            }
          };
          img.onerror = () => {
            drawPlaceholder(area);
            imagesLoaded++;
            if (imagesLoaded === totalImages) {
              drawFrameOnTop();
            }
          };
        } else {
          drawPlaceholder(area);
        }
      }

      // If no images to load, draw frame immediately
      if (totalImages === 0) {
        drawFrameOnTop();
      }
    };
    
    frameImg.onerror = () => {
      console.error('Frame image failed to load at', selectedFrame?.frameImage);
      console.log('Falling back to simple layout');
      // If frame fails to load, fall back to original behavior
      const singlePhotoWidth = 900;
      const singlePhotoHeight = Math.round(singlePhotoWidth * 4 / 3);
      canvas.width = singlePhotoWidth;
      canvas.height = singlePhotoHeight * 4;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const y = i * singlePhotoHeight;
        
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              singlePhotoWidth,
              singlePhotoHeight
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              0, y, singlePhotoWidth, singlePhotoHeight
            );
          };
          img.onerror = () => {
            drawPlaceholder({ x: 0, y: y, width: singlePhotoWidth, height: singlePhotoHeight });
          };
        } else {
          drawPlaceholder({ x: 0, y: y, width: singlePhotoWidth, height: singlePhotoHeight });
        }
      }
      // Draw a simple border as fallback
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
      setCanvasReady(true); // Mark canvas as ready even in fallback
    };
  }, [images, selectedFrame]);

  return (
  <div className="ResultScreen">      
    <div className="content-container">
      <div className="photo-frame-container">
        {images && images.length > 0 ? (
          <canvas 
            ref={canvasRef} 
            className="photo-strip"
            style={{
              maxHeight: '650px',
              width: 'auto',
              height: 'auto',
              border: 'none',
              boxShadow: 'none',
              background: 'transparent'
            }}
          />
        ) : (
          <p className="no-photos">No photos taken. Please try again.</p>
        )}
      </div>

      <div className="right-panel">
        <button 
          onClick={handleDownload} 
          className="download-button"
          disabled={!canvasReady}
          style={{ 
            opacity: canvasReady ? 1 : 0.5,
            cursor: canvasReady ? 'pointer' : 'not-allowed'
          }}
        >
          <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 0 24 24"
          width="20"
          fill="white"
          style={{ marginRight: '8px' }}
        >
          <path d="M5 20h14v-2H5v2zM12 2v12l4-4h-3V2h-2v8H8l4 4z" />
        </svg>
          Download Photostrip
        </button>

        <div class="hashtag-container">
          <p>Thanks for trying out this frame!</p>
          <p>Don't forget to share using <strong>#moa4cuts</strong> 💙</p>
        </div>

        <iframe 
          width="100%"
          height="315"
          src="https://www.youtube.com/embed/IcwHopeT5gY?si=c3fiZzQts6fVvVPb"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>

        <Link to="/" style={{ color: 'black', padding: '50px 12px' }}>
          🏠 Back to Home
        </Link>
      </div>
    </div>
  </div>
);

}

export default ResultScreenTMRWMember;